import { Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared page frame for every admin tab. Gives a consistent look across tabs:
 * - Horizontal padding steps up with the viewport (`px-4` → `sm:px-6` →
 *   `lg:px-8` → `2xl:px-12`) so gutters grow at set increments with screen size.
 * - Content is centered and capped (`mx-auto w-full max-w-7xl`) so it stays
 *   "not quite full width" on wide screens while the shell header spans full width.
 *
 * Tweak the two constants below to adjust the look for all tabs at once.
 */
const ADMIN_TAB_OUTER_CLASS =
  'min-h-full overflow-x-hidden bg-slate-50/40 px-4 py-6 sm:px-6 md:pt-8 lg:px-8 lg:pt-10 2xl:px-12';
const ADMIN_TAB_CONTENT_CLASS = 'mx-auto w-full max-w-7xl';

export function AdminTabContainer({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={ADMIN_TAB_OUTER_CLASS}>
      <div className={cn(ADMIN_TAB_CONTENT_CLASS, className)}>{children}</div>
    </div>
  );
}

/** Shared chrome for firm-admin tabs that are not API-wired yet. */
export function FirmAdminShell({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <AdminTabContainer className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        {action}
      </div>
      {children}
    </AdminTabContainer>
  );
}

export function FirmAdminEmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Users;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-slate-200 bg-white px-6 py-16 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-500">
        <Icon className="h-5 w-5" />
      </div>
      <div className="max-w-md space-y-1">
        <p className="text-sm font-medium text-slate-800">{title}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
    </div>
  );
}
